/**
 * FASE 5 / AV-3 — camada de acesso à API do Assistente Virtual.
 *
 * ┌───────────────────────────────────────────────────────────────────────────────────┐
 * │  MESMA EXCEÇÃO ARQUITETURAL APROVADA PARA `bi/biApi.js` — NÃO DISSOLVER.           │
 * │                                                                                   │
 * │  O `CLAUDE.md` do `web/` pede `$axios` direto no componente. Aqui vale a mesma     │
 * │  exceção de segurança: campos capazes de forjar identidade ou escopo precisam ser  │
 * │  proibidos num lugar só e de forma testável.                                       │
 * │                                                                                   │
 * │  ESCOPO: defesa ADICIONAL. A autoridade real é o backend                           │
 * │  (`AssistenteIdentidadeService::podeAcessar`, coberto por teste de IDOR e por      │
 * │  mutação). Um cliente que ignore este arquivo continua barrado lá.                 │
 * └───────────────────────────────────────────────────────────────────────────────────┘
 */

/**
 * Campos que o front está proibido de enviar.
 *
 * `nivel_identidade`, `verificado`, `aluno_id` e `cpf_verificado` decidem AUTORIZAÇÃO: se o
 * cliente pudesse mandá-los, bastaria o DevTools para se declarar verificado ou apontar
 * para o filho de outra pessoa.
 *
 * `aluno_id` é a exceção controlada: ele É enviado na consulta de fila, mas o backend o
 * confere contra o vínculo da sessão (IDOR). Por isso ele não entra na lista — o que entra
 * são os campos que NUNCA fazem sentido vindos do cliente.
 */
const CAMPOS_PROIBIDOS = [
  'nivel_identidade', 'verificado', 'origem_verificacao', 'cpf_verificado',
  'instituicoes_permitidas', 'user_id', 'homologavel', 'simulado',
  /*
   * AV 12 — a AUTORIA da curadoria e da publicação é decidida no servidor, a partir do
   * usuário autenticado. Se viesse do cliente, qualquer curador poderia assinar a decisão
   * com o nome de outra pessoa — e o registro de quem aprovou é metade do que o
   * versionamento serve para provar. O backend já ignora estes campos; aqui eles nem saem.
   */
  'curado_por', 'publicada_por', 'desativada_por', 'ativa', 'versao_publicada',
  /*
   * AV 23–28 — credenciais e situação do canal são decididas pelo AMBIENTE, no servidor.
   * Se o cliente pudesse mandá-las, bastaria o DevTools para declarar o canal credenciado
   * e a POC passaria a exibir "homologável" sem número nenhum na Meta.
   */
  'token', 'app_secret', 'verify_token', 'phone_number_id', 'api_key'
]

function limpar (dados) {
  const saida = {}
  Object.keys(dados || {}).forEach(function (k) {
    if (CAMPOS_PROIBIDOS.indexOf(k) !== -1) return
    const v = dados[k]
    if (v === null || v === undefined || v === '') return
    saida[k] = v
  })
  return saida
}

export default {
  criar (ctx) {
    const base = ctx.base
    const auth = ctx.auth
    const http = ctx.axios

    /** Rotas públicas: sem cabeçalho de autenticação, por desenho. */
    const publico = {}

    /*
     * SEGREDO DE SESSÃO.
     *
     * O `sessao_id` é sequencial e visível; sozinho ele não abre nada — o backend exige este
     * token em toda chamada que toque uma sessão. Ele é entregue UMA VEZ, na abertura, e
     * fica aqui dentro: não vai para a URL (ficaria no histórico e no log do servidor), não
     * vai para o `localStorage` e não é lido de volta pelos componentes.
     */
    let token = null

    /** Corpo de toda requisição que toca a sessão: id + segredo, nunca só o id. */
    const daSessao = function (sessaoId, extra) {
      // A identificação da sessão vai POR ÚLTIMO: assim um `extra` que traga `token` ou
      // `sessao_id` — vindo de um componente descuidado ou de um payload manipulado — é
      // sobrescrito, nunca o contrário.
      return Object.assign({}, extra || {}, { sessao_id: sessaoId, token: token })
    }

    return {
      // ============================================================ atendimento (público)
      carta () { return http.get(base + '/api/publico/assistente/carta', publico) },
      identidade () { return http.get(base + '/api/publico/assistente/identidade', publico) },

      iniciar (dados) {
        return http.post(base + '/api/publico/assistente/iniciar', limpar(dados), publico)
          .then(function (r) {
            // Única vez em que o token trafega. Guardado em memória e removido da resposta,
            // para que nenhum componente possa reemiti-lo ou registrá-lo em tela.
            if (r && r.data && r.data.token) {
              token = r.data.token
              delete r.data.token
            }
            return r
          })
      },
      mensagem (sessaoId, texto) {
        return http.post(base + '/api/publico/assistente/mensagem',
          daSessao(sessaoId, { texto: texto }), publico)
      },
      encerrar (sessaoId) {
        return http.post(base + '/api/publico/assistente/encerrar', daSessao(sessaoId), publico)
      },
      /*
       * A trilha leva o id na URL (é um GET), mas o segredo vai no CABEÇALHO — em query
       * string ele acabaria no histórico do navegador e no access log do servidor.
       */
      /* F5-FINAL-2 (F5) — o cidadão responde a data de nascimento da desambiguação. */
      desambiguar (sessaoId, dataNascimento) {
        return http.post(base + '/api/publico/assistente/desambiguar',
          { sessao_id: sessaoId, data_nascimento: dataNascimento }, publico)
      },
      trilha (sessaoId) {
        return http.get(base + '/api/publico/assistente/trilha/' + encodeURIComponent(sessaoId),
          { headers: { 'X-Assistente-Token': token } })
      },

      boletimPorChave (sessaoId, chave) {
        return http.post(base + '/api/publico/assistente/boletim/chave',
          daSessao(sessaoId, { chave: chave }), publico)
      },
      boletimPorResponsavel (sessaoId, cpf, dataNascimento) {
        return http.post(base + '/api/publico/assistente/boletim/responsavel',
          daSessao(sessaoId, { cpf: cpf, data_nascimento: dataNascimento }), publico)
      },
      fila (sessaoId, alunoId) {
        return http.post(base + '/api/publico/assistente/fila',
          daSessao(sessaoId, { aluno_id: alunoId }), publico)
      },
      preMatricula (sessaoId, dados) {
        return http.post(base + '/api/publico/assistente/pre-matricula',
          daSessao(sessaoId, limpar(dados)), publico)
      },
      /* AV 9 — o documento, sem depender do SMTP do município. Binário: precisa de blob. */
      comprovantePdf (sessaoId) {
        return http.post(base + '/api/publico/assistente/comprovante-pdf',
          daSessao(sessaoId), Object.assign({}, publico, { responseType: 'blob' }))
      },
      comprovante (sessaoId, email) {
        return http.post(base + '/api/publico/assistente/comprovante',
          daSessao(sessaoId, { email: email }), publico)
      },

      // ================================================================== Gov.br (público)
      govbrSituacao () { return http.get(base + '/api/publico/govbr/situacao', publico) },
      govbrIniciar (sessaoId) {
        return http.post(base + '/api/publico/govbr/iniciar', daSessao(sessaoId), publico)
      },

      // ============================================================ administração (auth)
      fluxos () { return http.get(base + '/api/assistente-admin/fluxos', auth) },
      arvore (fluxoId) {
        return http.get(base + '/api/assistente-admin/arvore/' + encodeURIComponent(fluxoId), auth)
      },
      validarFluxo (fluxoId) {
        return http.get(base + '/api/assistente-admin/validar/' + encodeURIComponent(fluxoId), auth)
      },
      publicarFluxo (fluxoId) {
        return http.post(base + '/api/assistente-admin/publicar/' + encodeURIComponent(fluxoId), {}, auth)
      },
      despublicarFluxo (fluxoId) {
        return http.post(base + '/api/assistente-admin/despublicar/' + encodeURIComponent(fluxoId), {}, auth)
      },
      salvarNo (fluxoId, dados) {
        return http.post(base + '/api/assistente-admin/no/' + encodeURIComponent(fluxoId), dados, auth)
      },
      removerNo (fluxoId, id) {
        return http.post(base + '/api/assistente-admin/no-remover/' + encodeURIComponent(fluxoId) +
          '/' + encodeURIComponent(id), {}, auth)
      },
      salvarTransicao (fluxoId, dados) {
        return http.post(base + '/api/assistente-admin/transicao/' + encodeURIComponent(fluxoId), dados, auth)
      },
      removerTransicao (fluxoId, id) {
        return http.post(base + '/api/assistente-admin/transicao-remover/' + encodeURIComponent(fluxoId) +
          '/' + encodeURIComponent(id), {}, auth)
      },
      textos (noId) {
        return http.get(base + '/api/assistente-admin/textos/' + encodeURIComponent(noId), auth)
      },
      salvarTexto (noId, dados) {
        return http.post(base + '/api/assistente-admin/texto/' + encodeURIComponent(noId), dados, auth)
      },
      removerTexto (noId, canal) {
        return http.post(base + '/api/assistente-admin/texto-remover/' + encodeURIComponent(noId) +
          '/' + encodeURIComponent(canal), {}, auth)
      },
      carta_admin () { return http.get(base + '/api/assistente-admin/carta', auth) },
      salvarServico (dados) {
        return http.post(base + '/api/assistente-admin/servico', dados, auth)
      },
      publicarServico (id) {
        return http.post(base + '/api/assistente-admin/servico-publicar/' + encodeURIComponent(id), {}, auth)
      },
      despublicarServico (id) {
        return http.post(base + '/api/assistente-admin/servico-despublicar/' + encodeURIComponent(id), {}, auth)
      },
      removerServico (id) {
        return http.post(base + '/api/assistente-admin/servico-remover/' + encodeURIComponent(id), {}, auth)
      },
      catalogos () { return http.get(base + '/api/assistente-admin/catalogos', auth) },
      painel (filtros) {
        return http.post(base + '/api/assistente-admin/painel', limpar(filtros), auth)
      },
      sessoes (filtros) {
        return http.get(base + '/api/assistente-admin/sessoes',
          Object.assign({ params: limpar(filtros) }, auth))
      },
      encerrarSessao (id) {
        return http.post(base + '/api/assistente-admin/sessao-encerrar/' + encodeURIComponent(id), {}, auth)
      },

      // ============================================ AV 12 — aprendizado (auth, curadoria)
      //
      // Nenhuma destas é pública, e é assim de propósito: o cidadão ALIMENTA a fila pelo
      // atendimento, sem saber; só o curador aprova e publica. Ver `AssistenteAprendizadoController`.
      aprendizadoFila (fluxoId, filtros) {
        return http.get(base + '/api/assistente-aprendizado/fila/' + encodeURIComponent(fluxoId),
          Object.assign({ params: limpar(filtros) }, auth))
      },
      aprendizadoCurar (id, dados) {
        // `curado_por` NÃO é enviado: a autoria vem do usuário autenticado, no servidor.
        return http.post(base + '/api/assistente-aprendizado/curar/' + encodeURIComponent(id),
          limpar(dados), auth)
      },
      aprendizadoCadastrar (fluxoId, dados) {
        return http.post(base + '/api/assistente-aprendizado/cadastrar/' + encodeURIComponent(fluxoId),
          limpar(dados), auth)
      },
      aprendizadoPublicar (fluxoId, resumo) {
        return http.post(base + '/api/assistente-aprendizado/publicar/' + encodeURIComponent(fluxoId),
          limpar({ resumo: resumo }), auth)
      },
      aprendizadoPainel (fluxoId) {
        return http.get(base + '/api/assistente-aprendizado/painel/' + encodeURIComponent(fluxoId), auth)
      },
      aprendizadoVersao (fluxoId, versao) {
        return http.get(base + '/api/assistente-aprendizado/versao/' + encodeURIComponent(fluxoId) +
          '/' + encodeURIComponent(versao), auth)
      },
      aprendizadoComparar (fluxoId, de, para) {
        return http.post(base + '/api/assistente-aprendizado/comparar/' + encodeURIComponent(fluxoId),
          { de: de, para: para }, auth)
      },
      aprendizadoReverter (fluxoId, versao, motivo) {
        return http.post(base + '/api/assistente-aprendizado/reverter/' + encodeURIComponent(fluxoId) +
          '/' + encodeURIComponent(versao), limpar({ motivo: motivo }), auth)
      },
      aprendizadoDesativar (fluxoId, motivo) {
        return http.post(base + '/api/assistente-aprendizado/desativar/' + encodeURIComponent(fluxoId),
          limpar({ motivo: motivo }), auth)
      },
      aprendizadoExplicar (sessaoId) {
        return http.get(base + '/api/assistente-aprendizado/explicar/' + encodeURIComponent(sessaoId), auth)
      },

      // ================================================ AV 23–28 — canal WhatsApp (auth)
      whatsappCanal () { return http.get(base + '/api/whatsapp-admin/canal', auth) },
      whatsappRelatorios (filtros) {
        return http.get(base + '/api/whatsapp-admin/relatorios',
          Object.assign({ params: limpar(filtros) }, auth))
      },
      whatsappRecebidas () { return http.get(base + '/api/whatsapp-admin/recebidas', auth) },
      whatsappPublicarPerfil () {
        return http.post(base + '/api/whatsapp-admin/publicar-perfil', {}, auth)
      },
      whatsappEmbed (dados) {
        return http.post(base + '/api/whatsapp-admin/embed', limpar(dados), auth)
      },

      // ================================================ AV 26 — identidade e avatar (auth)
      /*
       * ATENÇÃO AO NOME: `identidadeCanal`, não `identidade`.
       *
       * Havia DUAS chaves `identidade` neste mesmo objeto — a pública (linha ~87) e esta,
       * administrativa. Em JavaScript a segunda simplesmente sobrescreve a primeira, sem
       * aviso do compilador. A tela do cidadão chamava `api.identidade()` e caía aqui, com
       * `canal === undefined`, batendo em `/api/assistente-identidade/undefined`: HTTP 204,
       * corpo vazio, e o assistente aparecia com o nome padrão e sem avatar — a
       * customização do AV 26 nunca chegava a quem devia vê-la.
       */
      identidadeCanal (canal) {
        return http.get(base + '/api/assistente-identidade/' + encodeURIComponent(canal), auth)
      },
      identidadeTextos (canal, dados) {
        return http.post(base + '/api/assistente-identidade/textos/' + encodeURIComponent(canal),
          limpar(dados), auth)
      },
      /*
       * O upload vai como multipart. `limpar()` NÃO se aplica: o corpo é um FormData, e
       * filtrar campos dele exigiria remontá-lo — o que não acrescenta proteção nenhuma,
       * porque quem valida a imagem é o servidor, lendo o conteúdo do arquivo.
       */
      identidadeAvatar (canal, formData, headers) {
        return http.post(base + '/api/assistente-identidade/avatar-enviar/' + encodeURIComponent(canal),
          formData, headers)
      },
      identidadeAvatarRemover (canal) {
        return http.post(base + '/api/assistente-identidade/avatar-remover/'
          + encodeURIComponent(canal), {}, auth)
      },
      identidadePublicar (canal) {
        return http.post(base + '/api/assistente-identidade/publicar/'
          + encodeURIComponent(canal), {}, auth)
      },
      /** URL da imagem para o `<img>`. O token vai no cabeçalho da requisição do axios. */
      identidadeAvatarUrl (canal) {
        return base + '/api/assistente-identidade/avatar/' + encodeURIComponent(canal)
      }
    }
  },

  _limpar: limpar,
  _CAMPOS_PROIBIDOS: CAMPOS_PROIBIDOS
}
